import cx from 'classnames';
import React from 'react';
import { Link } from 'react-router-dom';

import { Icon, IconKebab, IconMaintenance, IconNewTab } from '@flixbus/honeycomb-icons-react';
import { Button, Dropdown, DropdownItem, Tag } from '@flixbus/honeycomb-react';

import { MODAL_NAMES } from 'common/activeElements/modals/modalNames';
import { deriveBreakdownSegment } from 'common/BreakdownDetails/deriveBreakdownSegment';
import { useActiveElements } from 'global/ActiveElements/useActiveElements';
import { buildBreakdownReasonsForAssignments } from 'pages/WorkAssignments/components/AssignmentModifications/utils';
import { RESOLUTION_TYPES } from 'pages/WorkAssignments/components/CreateBreakdownFlow/const';
import { BREAKDOWN_REASON_LABELS } from 'utils/breakdown';
import { formatTime, formatWeekdayDate } from 'utils/datetime';

import { BREAKDOWN_TEXT } from './text';

import styles from './styles.module.css';

const formatReason = (reason) => {
	if (!reason) return '';
	return BREAKDOWN_REASON_LABELS[reason] || reason.replace(/([A-Z])/g, ' $1').trim();
};

const BreakdownCard = ({ breakdown, timezone, className, stackedTime, sequences = [] }) => {
	const active = useActiveElements();
	const reasons = buildBreakdownReasonsForAssignments(breakdown).filter(
		(reason) =>
			reason.reason !== breakdown.reason && reason.reason !== RESOLUTION_TYPES.replacementCrewMember
	);

	const handleViewDetails = () => {
		const segment = deriveBreakdownSegment(breakdown, sequences);
		active.setActiveElement(MODAL_NAMES.breakdownDetails, { breakdown, segment });
	};

	return (
		<div className={className || styles.breakdownCard}>
			<div className={styles.breakdownHeader}>
				<Tag appearance="danger" display="outlined">
					<Icon InlineIcon={IconMaintenance} />
					{BREAKDOWN_TEXT.brokenDown}
				</Tag>
				<div className={styles.menu}>
					<Dropdown
						xPosition="left"
						yPosition="bottom"
						links={[
							<DropdownItem key="view-details" Elem="button" onClick={handleViewDetails}>
								{BREAKDOWN_TEXT.viewBreakdownDetails}
							</DropdownItem>,
						]}
					>
						<Button appearance="link" display="square" aria-label={BREAKDOWN_TEXT.menuAriaLabel}>
							<Icon InlineIcon={IconKebab} />
						</Button>
					</Dropdown>
				</div>
			</div>
			{breakdown.incidentTimestamp && (
				<div className={cx(styles.breakdownRow, { [styles.breakdownRowStacked]: stackedTime })}>
					<span className={styles.label}>{BREAKDOWN_TEXT.timeOfIncident}</span>
					<span>
						{`${formatWeekdayDate(breakdown.incidentTimestamp, timezone)} · ${formatTime(breakdown.incidentTimestamp, { timezone, showTimezone: true })}`}
					</span>
				</div>
			)}
			{breakdown.reason && (
				<div className={styles.breakdownRow}>
					<span className={styles.label}>{BREAKDOWN_TEXT.reason}</span>
					<span>{formatReason(breakdown.reason)}</span>
				</div>
			)}
			{reasons.map((reason) => (
				<div key={reason.reason} className={styles.breakdownRow}>
					<span className={styles.label}>{BREAKDOWN_TEXT.replacementType}</span>
					<span>
						{reason.reason === RESOLUTION_TYPES.deadhead
							? BREAKDOWN_TEXT.deadhead
							: BREAKDOWN_TEXT.parallelRide}
					</span>
					{reason.linkTo && (
						<Link
							to={reason.linkTo}
							target="_blank"
							rel="noopener noreferrer"
							className={styles.link}
						>
							<Icon InlineIcon={IconNewTab} />
						</Link>
					)}
				</div>
			))}
		</div>
	);
};

export default BreakdownCard;
